import React, { useEffect, useRef, useState } from "react";
import "./VideoTrimmer.css";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import videojs from "video.js";
import { useClipping } from "modules/v2Clipping/hooks/useClipping";
import {
  clipping,
  clippingStatus
} from "modules/v2Clipping/services/clipping.service";
import { formatTime } from "modules/core/time/utils";
import { Flag } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";

interface VideoTrimmerI {
  playerMain: any;
  source: string | undefined;
  title: string;
}

const VideoTrimmer = ({ playerMain, source, title }: VideoTrimmerI) => {
  const [playerTime, setPlayerTime] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [clip, setClip] = useState<any>();
  const [status, setStatus] = useState(null);
  const [uri, setUri] = useState(null);

  const createClip = async () => {
    console.log("create clip");
    if (!source) return;
    if (start > end) return;

    const clipUuid = uuidv4();
    const body = {
      source,
      start: start,
      duration: end - start,
      uuid: clipUuid,
      output: title + "_" + clipUuid
    };

    console.log(body);

    await clipping(body).then((data) => {
      setClip(data);
    });
  };

  useEffect(() => {
    if (!clip?.uuid) return;

    const pollingInterval = 5000; // 5 seconds

    const fetchClipStatus = async () => {
      try {
        const response = await clippingStatus(clip.uuid);
        setStatus(response);

        if (response.uri) {
          setUri(response.uri);
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Error fetching clip status:", error);
      }
    };

    // Start polling
    const intervalId = setInterval(fetchClipStatus, pollingInterval);

    // Clear interval on component unmount
    return () => clearInterval(intervalId);
  }, [clip?.uuid]);

  const pauseVideo = () => {
    if (playerMain?.paused()) {
      playerMain?.play();
    } else {
      playerMain?.pause();
    }

    setPlayerTime(playerMain?.currentTime());
  };

  const rewind = () => {
    playerMain?.currentTime(playerMain?.currentTime() - 5);
    setPlayerTime(playerMain?.currentTime());
  };

  const forward = () => {
    playerMain?.currentTime(playerMain?.currentTime() + 5);
    console.log("FOR", playerMain?.currentTime());
    setPlayerTime(playerMain?.currentTime());
  };

  const setDurations = (type: string) => {
    pauseVideo();
    if (type === "start") {
      setStart(playerMain?.currentTime());
    } else {
      setEnd(playerMain?.currentTime());
    }
  };

  return (
    <div className="videoTrimmer">
      <Container id="trimControls" style={{ display: "block" }}>
        {/* Timeline Controls */}
        <Row className="align-items-center">
          <Col md={4} className="text-start">
            <div>
              {formatTime(playerMain?.currentTime())} /{" "}
              {playerMain?.duration()
                ? formatTime(playerMain?.duration())
                : "00:00:00"}
            </div>
          </Col>
          <Col md={4} className="text-center">
            <Button className="btn control-btn" onClick={() => rewind()}>
              <img src="/rewind-fill.svg" alt="Rewind" />
            </Button>
            <Button className="btn control-btn" onClick={() => pauseVideo()}>
              <img src="/play-fill.svg" alt="Play" />
            </Button>
            <Button className="btn control-btn" onClick={() => forward()}>
              <img src="/speed-fill.svg" alt="Forward" />
            </Button>
          </Col>
          <Col md={4} className="text-end">
            <div className="timeline-zoom-control d-flex align-items-center justify-content-end">
              <img
                src="/expand-width-fill.svg"
                alt="Expand"
                className="trimm"
                style={{ marginRight: "10px" }}
              />
              <Button className="zoom-btn" id="zoomOut">
                -
              </Button>
              <input
                type="range"
                id="zoomSlider"
                min="1"
                max="100"
                defaultValue="50"
                className="zoom-slider"
                disabled
              />
              <Button className="zoom-btn" id="zoomIn">
                +
              </Button>
            </div>
          </Col>
        </Row>
        <hr />

        <Row>
          <Row xs={12}>
            <div className="canvas-container">
              <div className="play-locator">
                <img
                  src="/vip-diamond-fill.svg"
                  className="play-loc"
                  alt="Play Locator"
                  style={{ cursor: "grab" }}
                />
              </div>
              <div
                className="timeline-container"
                style={{ position: "relative", height: "70px" }}
              >
                {/* Main Timeline Metrics */}
                <div
                  className="timeline-metrics"
                  style={{ position: "absolute", width: "100%", top: "0px" }}
                >
                  <span style={{ position: "absolute", left: "0%" }}>0m</span>
                  <span
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)"
                    }}
                  >
                    1m
                  </span>
                  <span style={{ position: "absolute", right: "0%" }}>2m</span>
                </div>

                {/* Additional Points on the Timeline */}
                <div
                  style={{ position: "absolute", width: "100%", top: "0px" }}
                >
                  <span style={{ position: "absolute", left: "10%" }}>•</span>
                  <span style={{ position: "absolute", left: "20%" }}>•</span>
                  <span style={{ position: "absolute", left: "30%" }}>•</span>
                  <span style={{ position: "absolute", left: "40%" }}>•</span>
                  <span style={{ position: "absolute", left: "60%" }}>•</span>
                  <span style={{ position: "absolute", left: "70%" }}>•</span>
                  <span style={{ position: "absolute", left: "80%" }}>•</span>
                  <span style={{ position: "absolute", left: "90%" }}>•</span>
                </div>
                <hr
                  style={{ position: "absolute", width: "100%", top: "10px" }}
                />
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "40px",
                    top: "33px",
                    background: "url('/frame_by_frame_slider.jpg')",
                    backgroundSize: "cover",
                    backgroundPosition: "left",
                    border: "1px solid #03A9F4",
                    borderRadius: "6px",
                    minWidth: "60px"
                  }}
                ></div>
                <hr
                  style={{ position: "absolute", width: "100%", top: "63px" }}
                />
              </div>
            </div>

            <Col xs={6}>
              <Form.Group controlId="formLastName">
                <Form.Label>Start</Form.Label>
                <div style={{ display: "flex" }}>
                  <Form.Control
                    type="text"
                    name="last-name"
                    value={formatTime(start)}
                    style={{
                      background: "#000",
                      color: "#fff",
                      borderColor: "grey"
                    }}
                  />
                  <Button onClick={() => setDurations("start")}>
                    <Flag />
                  </Button>
                </div>
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group controlId="formLastName">
                <Form.Label>End</Form.Label>
                <div style={{ display: "flex" }}>
                  <Form.Control
                    type="text"
                    name="last-name"
                    value={formatTime(end)}
                    style={{
                      background: "#000",
                      color: "#fff",
                      borderColor: "grey"
                    }}
                  />
                  <Button onClick={() => setDurations("end")}>
                    <Flag />
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Col xs={12} className="mt-4 text-end pe-4">
            <Button
              className="edit-btn"
              onClick={() => (uri ? window.open(uri) : createClip())}
            >
              <img src="/video-download-line.svg" alt="Download" />
            </Button>
            <span className="edit-text">
              {uri ? "Download" : clip?.uuid ? "Generating" : "Create"} your{" "}
              <a
                style={{
                  color: uri ? "red" : "#fff",
                  textDecoration: "none",
                  marginRight: "10px"
                }}
                target="_blank"
                href={uri ?? "#"}
              >
                Video
              </a>
              <span style={{ color: "rgb(142, 142, 142)" }}>
                ({formatTime(start)} - {formatTime(end)}){" "}
              </span>
            </span>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VideoTrimmer;
